import { t } from 'i18next';
import { Popover } from '@c2fo/liquidity';
import { InfoCircleIcon } from '@c2fo/liquidity/icons';
import { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';

export type FundedByPartnerDisplayProps =
  | {
      buyer: BuyerGroupWithStats;
    }
  | {
      takerMarket: TakerMarket;
    };

const FundedByPartnerLabel = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <div className="flex gap-1 text-xs font-medium text-blue-500">
    {label}
    <Popover>
      <Popover.Trigger>
        <InfoCircleIcon className="h-3 w-3 fill-blue-500" />
      </Popover.Trigger>
      <Popover.Content arrow collisionPadding={24} className="max-w-52 text-center text-sm" side="top">
        {tooltip}
      </Popover.Content>
    </Popover>
  </div>
);

const FundedByPartnerDisplay = (props: FundedByPartnerDisplayProps) => {
  const buyer = 'buyer' in props ? props.buyer : undefined;
  const takerMarket = 'takerMarket' in props ? props.takerMarket : undefined;

  if (buyer) {
    const hasAnyDSFMarkets = buyer.takerMarkets.some((tm) => tm.marketIsDSF);

    // If there are no DSF markets, don't render anything
    if (!hasAnyDSFMarkets) {
      return null;
    }

    const allMarketsAreDSF = buyer.takerMarkets.every((tm) => tm.marketIsDSF);

    // If all markets are DSF, show the funded by bank partner information
    // If not all markets are DSF, show the partially funded by bank partner information
    const label = allMarketsAreDSF
      ? t('builder.fundedByBankPartnerLabel')
      : t('builder.partiallyFundedByBankPartnerLabel');

    const tooltip = allMarketsAreDSF
      ? t('builder.fundedByBankPartnerTooltip')
      : t('builder.partiallyFundedByBankPartnerTooltip');

    return <FundedByPartnerLabel label={label} tooltip={tooltip} />;
  }

  if (takerMarket) {
    const isDSFTakerMarket = takerMarket.marketIsDSF;

    // If the taker market is not DSF, don't render anything
    if (!isDSFTakerMarket) {
      return null;
    }

    return (
      <FundedByPartnerLabel
        label={t('builder.fundedByBankPartnerLabel')}
        tooltip={t('builder.fundedByBankPartnerTooltip')}
      />
    );
  }

  return null;
};

export default FundedByPartnerDisplay;
